import type { OperatingPeriod } from '../../../../types/simulation';
import type { Vehicle } from '../../../../types/vehicle';
import type { GeneratedShift } from './types';
import { calculateTimeDifference, formatTime, parseTime } from './timeUtils';

export type TimelineSlotStatus = 'working' | 'break' | 'off';
export type TimelineSlotOverrides = Record<string, TimelineSlotStatus>;

const DEFAULT_MAX_SHIFT_TIME_MINUTES = 8 * 60;

export function toTimelineSlotKey(vehicleId: string, blockStartMinutes: number): string {
  return `${vehicleId}:${blockStartMinutes}`;
}

export function getNextTimelineStatus(current: TimelineSlotStatus): TimelineSlotStatus {
  if (current === 'working') return 'break';
  if (current === 'break') return 'off';
  return 'working';
}

export function getOperatingPeriodStatusAtBlock(
  periods: OperatingPeriod[],
  blockStartMinutes: number,
  intervalMinutes: number
): TimelineSlotStatus {
  const blockEndMinutes = blockStartMinutes + intervalMinutes;
  for (const period of periods) {
    const periodStart = parseTime(period.start);
    const periodEnd = parseTime(period.end);
    if (blockStartMinutes >= periodStart && blockStartMinutes < periodEnd) {
      for (const breakPeriod of period.breaks || []) {
        const breakStart = parseTime(breakPeriod.start);
        const breakEnd = parseTime(breakPeriod.end);
        if (blockStartMinutes < breakEnd && blockEndMinutes > breakStart) {
          return 'break';
        }
      }
      return 'working';
    }
  }
  return 'off';
}

export function splitOperatingPeriodsForMaxShift(
  periods: Array<{ start: string; end: string; breaks?: Array<{ start: string; end: string; location: string }> }>,
  maxShiftMinutes: number = DEFAULT_MAX_SHIFT_TIME_MINUTES
): OperatingPeriod[] {
  const segments: OperatingPeriod[] = [];
  periods.forEach((period) => {
    const periodStart = parseTime(period.start);
    const periodEnd = parseTime(period.end);
    const periodBreaks = period.breaks || [];
    if (!Number.isFinite(periodStart) || !Number.isFinite(periodEnd) || periodEnd <= periodStart) {
      return;
    }
    for (let cursor = periodStart; cursor < periodEnd; cursor += maxShiftMinutes) {
      const segmentEnd = Math.min(periodEnd, cursor + maxShiftMinutes);
      const segmentBreaks = periodBreaks
        .map((breakPeriod) => {
          const breakStart = parseTime(breakPeriod.start);
          const breakEnd = parseTime(breakPeriod.end);
          const overlaps = breakStart < segmentEnd && breakEnd > cursor;
          if (!overlaps) return null;
          return {
            start: formatTime(Math.max(cursor, breakStart)),
            end: formatTime(Math.min(segmentEnd, breakEnd)),
            location: breakPeriod.location || 'On route',
          };
        })
        .filter(
          (breakPeriod): breakPeriod is { start: string; end: string; location: string } =>
            breakPeriod !== null && parseTime(breakPeriod.end) > parseTime(breakPeriod.start)
        );
      segments.push({
        start: formatTime(cursor),
        end: formatTime(segmentEnd),
        breaks: segmentBreaks,
      });
    }
  });
  return segments;
}

export function buildOperatingPeriodsFromStatuses(
  statuses: TimelineSlotStatus[],
  serviceStartMinutes: number,
  intervalMinutes: number,
  maxShiftMinutes: number = DEFAULT_MAX_SHIFT_TIME_MINUTES
): OperatingPeriod[] {
  const periods: Array<{ start: string; end: string; breaks: Array<{ start: string; end: string; location: string }> }> = [];
  let currentPeriodStart: number | null = null;
  let currentBreakStart: number | null = null;
  let currentBreaks: Array<{ start: string; end: string; location: string }> = [];

  statuses.forEach((status, index) => {
    const blockStart = serviceStartMinutes + index * intervalMinutes;
    const blockEnd = blockStart + intervalMinutes;
    const isOff = status === 'off';
    const isBreak = status === 'break';

    if (!isOff && currentPeriodStart === null) {
      currentPeriodStart = blockStart;
    }
    if (isBreak && currentBreakStart === null) {
      currentBreakStart = blockStart;
    }
    if (!isBreak && currentBreakStart !== null) {
      currentBreaks.push({
        start: formatTime(currentBreakStart),
        end: formatTime(blockStart),
        location: 'On route',
      });
      currentBreakStart = null;
    }
    if (isOff && currentPeriodStart !== null) {
      if (currentBreakStart !== null) {
        currentBreaks.push({
          start: formatTime(currentBreakStart),
          end: formatTime(blockStart),
          location: 'On route',
        });
        currentBreakStart = null;
      }
      periods.push({
        start: formatTime(currentPeriodStart),
        end: formatTime(blockStart),
        breaks: currentBreaks,
      });
      currentPeriodStart = null;
      currentBreaks = [];
    }

    if (index === statuses.length - 1 && currentPeriodStart !== null) {
      if (currentBreakStart !== null) {
        currentBreaks.push({
          start: formatTime(currentBreakStart),
          end: formatTime(blockEnd),
          location: 'On route',
        });
      }
      periods.push({
        start: formatTime(currentPeriodStart),
        end: formatTime(blockEnd),
        breaks: currentBreaks,
      });
    }
  });

  const normalized = periods.filter((period) => parseTime(period.end) > parseTime(period.start));
  return splitOperatingPeriodsForMaxShift(normalized, maxShiftMinutes);
}

export function recalculateFleetSummary(vehicles: Vehicle[], serviceStartTime: string, serviceEndTime: string) {
  const totalBreaks = vehicles.reduce(
    (sum, vehicle) => sum + vehicle.operatingHours.reduce((inner, period) => inner + (period.breaks?.length || 0), 0),
    0
  );

  let activeMinutes = 0;
  vehicles.forEach((vehicle) => {
    vehicle.operatingHours.forEach((period: OperatingPeriod) => {
      activeMinutes += calculateTimeDifference(parseTime(period.start), parseTime(period.end));
      (period.breaks || []).forEach((breakPeriod) => {
        activeMinutes -= calculateTimeDifference(parseTime(breakPeriod.start), parseTime(breakPeriod.end));
      });
    });
  });

  const serviceDuration = calculateTimeDifference(parseTime(serviceStartTime), parseTime(serviceEndTime));
  const totalVehicleHours = (serviceDuration * vehicles.length) / 60;
  const activeVehicleHours = activeMinutes / 60;
  const utilizationPercent = totalVehicleHours > 0 ? (activeVehicleHours / totalVehicleHours) * 100 : 0;

  return {
    totalVehicles: vehicles.length,
    totalShifts: vehicles.reduce((sum, vehicle) => sum + vehicle.operatingHours.length, 0),
    totalBreaks,
    servicePeriod: `${serviceStartTime} - ${serviceEndTime}`,
    totalStandardSeats: vehicles.reduce((sum, vehicle) => sum + (vehicle.standardSeats || 0), 0),
    totalWheelchairSeats: vehicles.reduce((sum, vehicle) => sum + (vehicle.wheelchairSeats || 0), 0),
    totalCapacity: vehicles.reduce((sum, vehicle) => sum + (vehicle.standardSeats || 0) + (vehicle.wheelchairSeats || 0), 0),
    activeVehicleHours: Math.round(activeVehicleHours * 10) / 10,
    totalVehicleHours: Math.round(totalVehicleHours * 10) / 10,
    utilizationPercent: Math.round(utilizationPercent * 10) / 10,
  };
}

export function convertVehiclesToGeneratedShifts(vehicles: Vehicle[]): GeneratedShift[] {
  return vehicles.map((vehicle) => ({
    vehicleId: vehicle.id,
    vehicleName: vehicle.name,
    operatingPeriods: vehicle.operatingHours || [],
  }));
}
